import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import useContextStore, { selectCurrentContext } from '../../store/useContextStore';
import useMyProfileStore from '../../store/useMyProfileStore';
import useAuthStore from '../../store/useAuthStore';
import { tabNavForContext, moreNavForContext, PATHS } from '../../routes/navConfig';
import { IconMenu, IconClose, IconSignOut, IconUsers } from '../icons';
import { APP_VERSION } from '../../utils/constants';

/* Phone navigation (the sidebar is hidden below 760px): the most-used sections
   get a tab each, everything else opens in the More sheet. Profile and Sign Out
   live there too — with no sidebar there is nowhere else for them. */
export default function MobileNav() {
  const ctx = useContextStore(selectCurrentContext);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const tabs = tabNavForContext(ctx);
  const more = moreNavForContext(ctx);

  /* the sheet never outlives the page it was opened from */
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const signOut = () => {
    useMyProfileStore.getState().reset();
    useContextStore.getState().resetContexts();
    logout();
    navigate(PATHS.LOGIN);
  };

  /* More owns the current page when it isn't one of the tabs */
  const moreIsCurrent =
    location.pathname === PATHS.PROFILE || more.some((n) => n.path === location.pathname);

  return (
    <>
      <nav className="tabbar" aria-label="Sections">
        {tabs.map(({ path, label, short, Icon }) => (
          <NavLink key={path} to={path} className={({ isActive }) => `tab-item ${isActive ? 'on' : ''}`}>
            <Icon />
            <span>{short || label}</span>
          </NavLink>
        ))}
        {!!more.length && (
          <button
            type="button"
            className={`tab-item ${moreIsCurrent ? 'on' : ''}`}
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <IconMenu />
            <span>More</span>
          </button>
        )}
      </nav>

      {!!more.length && (
        <div className={`more-overlay ${open ? 'on' : ''}`} onClick={() => setOpen(false)}>
          <div
            className="more-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="More sections"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="more-grip" />
            <div className="more-head">
              <h3>More</h3>
              <button type="button" className="more-close" aria-label="Close" onClick={() => setOpen(false)}>
                <IconClose />
              </button>
            </div>
            <div className="more-list">
              {more.map(({ path, label, Icon }) => (
                <NavLink key={path} to={path} className={({ isActive }) => `more-item ${isActive ? 'on' : ''}`}>
                  <Icon />
                  {label}
                </NavLink>
              ))}
              <div className="more-sep" />
              <NavLink to={PATHS.PROFILE} className={({ isActive }) => `more-item ${isActive ? 'on' : ''}`}>
                <IconUsers />
                My Profile
              </NavLink>
              <button type="button" className="more-item" onClick={signOut}>
                <IconSignOut />
                Sign Out
              </button>
            </div>
            <div className="more-version">{APP_VERSION}</div>
          </div>
        </div>
      )}
    </>
  );
}
